import React, { useState } from 'react';
import { Container, Typography, Button } from '@sm/wds-react';
import { Slide } from '@sm/wds-motion';
import { IconX } from '@sm/wds-icons';
import {
  BottomBarThemes,
  BottomBarCtaColors,
  BottomBarCtaVariants,
} from './BottomBarEnums';
import BottomBarSkeletonLoader from './BottomBarSkeleton';
import './bottom-bar.scss';
import { useContentModuleEvent } from '../../hooks/useContentModuleEvent';
import { ContentModuleEvent } from '../../types/enums';
import { ContentModuleProperties } from '../../types/types';

export type BottomBarProps = {
  message: string;
  ctaText: string;
  ctaLink: string;
  theme?: BottomBarThemes;
  ctaNewTab?: boolean;
  ctaColor?: BottomBarCtaColors;
  ctaVariant?: BottomBarCtaVariants;
  dismissable?: boolean;
  loading?: boolean;
};

export const BottomBar: React.FC<BottomBarProps> = ({
  message,
  ctaText,
  ctaLink,
  theme,
  ctaNewTab,
  ctaColor,
  ctaVariant,
  dismissable,
  loading,
}) => {
  const [show, setShow] = useState(true);
  const recordModuleEvent = useContentModuleEvent();
  const handleDismiss = () => {
    setShow(false);
    recordModuleEvent(ContentModuleEvent.MODULE_DISMISSED);
  };
  const handleCtaClick = (e: Event, ctaLink: string) => {
    e.preventDefault();
    const properties: ContentModuleProperties = {
      destination: ctaLink,
    };
    recordModuleEvent(ContentModuleEvent.CTA_CLICKED, properties);
    if (ctaNewTab) {
      window.open(ctaLink);
    } else {
      window.location.assign(ctaLink);
    }
  };

  const smBottomClass = 'sm-bottom-bar';

  const bottomBarContent = (
    <Container
      className={`${smBottomClass} ${smBottomClass}--${theme}`}
      data-testid="theme-wrapper"
      p="6"
    >
      <Container
        flex
        flexDirection="row"
        flexAlign="center"
        flexJustify="center"
        px="9"
      >
        <Typography
          className={`${smBottomClass}__text ${smBottomClass}--${theme}__text`}
        >
          {message}
        </Typography>
        <Button
          className="wds-m-l-5"
          onClick={(e: Event) => handleCtaClick(e, ctaLink)}
          href={ctaLink}
          target={ctaNewTab ? '_blank' : undefined}
          rel={ctaNewTab ? 'noreferrer noopener' : undefined}
          size="sm"
          color={ctaColor}
          variant={ctaVariant}
        >
          {ctaText}
        </Button>
      </Container>
      {dismissable ? (
        <Button
          className={`${smBottomClass}__button-wrapper`}
          color={theme === BottomBarThemes.white ? 'black' : 'muted'}
          data-testid="dismiss-button"
          onClick={handleDismiss}
          variant="text"
        >
          <IconX />
        </Button>
      ) : null}
    </Container>
  );

  return (
    <>
      {loading ? (
        <BottomBarSkeletonLoader />
      ) : dismissable ? (
        <Slide direction="up" in={show}>
          {bottomBarContent}
        </Slide>
      ) : (
        <>{bottomBarContent}</>
      )}
    </>
  );
};

BottomBar.defaultProps = {
  theme: BottomBarThemes.white,
  ctaNewTab: false,
  ctaColor: BottomBarCtaColors.alt,
  ctaVariant: BottomBarCtaVariants.solid,
  dismissable: false,
  loading: false,
};

export default BottomBar;
